import React, { useState } from 'react';
import Select from 'react-select';
import { trainTypeIcons, categoryLabels } from './trainUtils';
import { TYPE_FILTERS, selectStyles } from './MapControls';

/**
 * Search box above the map. Results do not open here — they go into the right-hand panel,
 * the same slot the train, station and KBS views use, so only one thing is ever open.
 */
const MapSearch = ({ options, opDay, compact = false, onSearch, onClear, busy, resultCount }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // On a phone the search is a pill until tapped — a permanent search bar would eat a
  // sixth of the map
  const [expanded, setExpanded] = useState(false);
  const [operatingDate, setOperatingDate] = useState('');
  const [line, setLine] = useState(null);
  const [serie, setSerie] = useState(null);
  const [category, setCategory] = useState('');

  const activeFilters = (operatingDate ? 1 : 0) + (line ? 1 : 0) + (serie ? 1 : 0) + (category ? 1 : 0);

  const submit = (e) => {
    e?.preventDefault();
    if (!query.trim() && activeFilters === 0) return;
    setExpanded(false);
    onSearch({
      query: query.trim(),
      // Without an explicit day the search follows the operating day the map is showing
      operatingDate: operatingDate || opDay,
      trainLine: line?.value || null,
      trainSerie: serie?.value || null,
      trainTypeProductCategorie: TYPE_FILTERS.find(t => t.type === category)?.apiValue || null,
    });
  };

  const reset = () => {
    setQuery('');
    setOperatingDate('');
    setLine(null);
    setSerie(null);
    setCategory('');
    onClear();
  };

  if (compact && !expanded) {
    return (
      <button
        type="button"
        className="map-search-pill"
        onClick={() => setExpanded(true)}
        aria-label="Zug suchen"
      >
        <i className="fa fa-search"></i>
        <span>{query || 'Zug suchen'}</span>
        {(activeFilters > 0 || resultCount > 0) && (
          <span className="map-search-badge">{activeFilters || resultCount}</span>
        )}
      </button>
    );
  }

  return (
    <form className={`map-search ${compact ? 'map-search-sheet' : ''}`} onSubmit={submit}>
      {compact && (
        <button
          type="button"
          className="map-search-back"
          onClick={() => setExpanded(false)}
          aria-label="Suche schließen"
        >
          <i className="fa fa-chevron-down"></i> Suche schließen
        </button>
      )}
      <div className="map-search-row">
        <i className="fa fa-search map-search-icon"></i>
        <input
          type="text"
          className="map-search-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Zug suchen — ICE 601, RE 1, 4711"
        />
        {(query || activeFilters > 0 || resultCount > 0) && (
          <button type="button" className="map-search-clear" onClick={reset} title="Zurücksetzen">
            <i className="fa fa-times"></i>
          </button>
        )}
        <button
          type="button"
          className={`map-search-filter-btn ${open ? 'open' : ''} ${activeFilters ? 'has-active' : ''}`}
          onClick={() => setOpen(o => !o)}
          title="Filter"
        >
          <i className="fa fa-sliders"></i>
          {activeFilters > 0 && <span className="map-search-badge">{activeFilters}</span>}
        </button>
        <button type="submit" className="map-search-submit" disabled={busy}>
          {busy ? <i className="fa fa-spinner fa-spin"></i> : 'Suchen'}
        </button>
      </div>

      {open && (
        <div className="map-search-filters">
          <div className="map-field-row">
            <label className="map-label">Betriebstag</label>
            <input
              type="date"
              className="map-input"
              value={operatingDate}
              onChange={e => setOperatingDate(e.target.value)}
            />
          </div>
          <div className="map-field-row">
            <label className="map-label">Linie</label>
            <div className="map-select">
              <Select
                options={options.lines}
                value={line}
                onChange={setLine}
                isClearable
                placeholder="Alle Linien"
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
          </div>
          <div className="map-field-row">
            <label className="map-label">Baureihe</label>
            <div className="map-select">
              <Select
                options={options.series}
                value={serie}
                onChange={setSerie}
                isClearable
                placeholder="Alle Baureihen"
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
          </div>
          <div className="map-field-row">
            <label className="map-label">Kategorie</label>
            <div className="map-type-filters">
              {TYPE_FILTERS.map(tf => (
                <button
                  key={tf.type}
                  type="button"
                  className={`map-type-btn ${category === tf.type ? 'active' : ''}`}
                  onClick={() => setCategory(c => (c === tf.type ? '' : tf.type))}
                  title={tf.label || categoryLabels[tf.apiValue]}
                >
                  {trainTypeIcons[tf.type]}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default MapSearch;
